import { inject, Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { switchMap } from 'rxjs/operators';
import { ContactFilterInput, DateTime, JobItemFilterInput, Uuid } from '../../../graphql';
import { Job, PurchaseOrderCreationSource } from '../../data/models';
import { IntegrationProvider } from '../../data/models/integrations.model';
import { UpdateJobCustomerDetailsModel } from '../../data/models/job.model';
import { RepoJobsService } from '../../data/repositories/repo-jobs.service';
import { JOBS_V2_NAME } from '../../graphql.module';
import { GetEstimateItem } from '../../pages/estimates/estimates-list2/domain/queries/GetEstimateItem.generated';
import {
    InputsForPurchaseOrderImport,
    InputsForPurchaseOrderImportVariables,
} from '../../pages/jobs/purchase-orders/domain/api/queries/InputsForPurchaseOrderImport.generated';
import {
    SearchPurchaseOrders,
    SearchPurchaseOrdersVariables,
} from '../../pages/jobs/purchase-orders/domain/api/queries/SearchPurchaseOrders.generated';
import { PurchaseOrdersService } from '../../pages/jobs/purchase-orders/purchase-orders.service';
import { UPDATE_JOB_CUSTOMER, UPDATE_JOB_CUSTOMER_DETAILS } from '../../shared/graphql/jobsV2_queries';
import { BxLogger } from './bx-logger.service';
import { ApolloBaseApiService } from './graph-ql';
import { firstValueFrom } from 'rxjs';
import { QuoteRequestRecipientApiService } from '../../pages/quoterequests/api/quote-request-recipient-api.service';

@Injectable({ providedIn: 'root' })
export class JobsV2Service extends ApolloBaseApiService {
    #quoteRequestRecipientApiService = inject(QuoteRequestRecipientApiService);

    constructor(
        apollo: Apollo,
        private purchaseOrdersService: PurchaseOrdersService,
        private jobsRepo: RepoJobsService,
        private logger: BxLogger
    ) {
        super(apollo, JOBS_V2_NAME);
    }

    searchPurchaseOrders(request: SearchPurchaseOrdersVariables['request']) {
        return this.graphqlGateway.mutate({
            mutation: SearchPurchaseOrders,
            variables: { request },
            fetchPolicy: 'no-cache',
        });
    }

    updateJobCustomer(jobId: string, customerId: string) {
        return this.graphqlGateway.mutate<{ updateJobCustomer: boolean }>({
            mutation: UPDATE_JOB_CUSTOMER,
            variables: { jobId, customerId },
            fetchPolicy: 'no-cache',
        });
    }

    updateJobCustomerDetails(jobId: string, model: UpdateJobCustomerDetailsModel) {
        return this.graphqlGateway.mutate<{ updateJobCustomerDetails: boolean }>({
            mutation: UPDATE_JOB_CUSTOMER_DETAILS,
            variables: { jobId, model },
            fetchPolicy: 'no-cache',
        });
    }

    createPurchaseOrderFromImportedBill(createOrderFromBill: CreateOrderFromBill, purchaseOrderId: Uuid | undefined) {
        const creatingNewPurchaseOrder = !purchaseOrderId;
        const jobItemIds = new Set<Uuid>();
        createOrderFromBill.items.forEach((item) => {
            if (item.categoryId) jobItemIds.add(item.categoryId);
            if (item.jobItemId) jobItemIds.add(item.jobItemId);
        });
        if (createOrderFromBill.jobItemId) jobItemIds.add(createOrderFromBill.jobItemId);

        const jobItemsWhere: JobItemFilterInput = {
            and: [{ iD_JobItem: { in: Array.from(jobItemIds) } }, { isDeleted: { neq: true } }],
        };

        const contactWhere: ContactFilterInput = {
            and: [{ accountingId: { eq: createOrderFromBill.contactId } }, { isDeleted: { neq: true } }],
        };

        const inputsForPurchaseImportVariables: InputsForPurchaseOrderImportVariables = {
            jobId: createOrderFromBill.jobId,
            jobItemsWhere,
            contactWhere,
            includeContact: !!createOrderFromBill.contactId,
        };

        return this.graphqlGateway
            .query({
                query: InputsForPurchaseOrderImport,
                variables: inputsForPurchaseImportVariables,
                fetchPolicy: 'no-cache',
                errorPolicy: 'all',
            })
            .pipe(
                switchMap(async ({ data }) => {
                    const { job, jobItemsByJobId, contact: contactBasedOnBill } = data;

                    if (!job) {
                        this.logger.error('Failed to import bill for the chosen job.');
                        return;
                    }

                    const jobItemsMap = new Map(jobItemsByJobId?.items?.map((item) => [item.iD_JobItem, item]));
                    let purchaseOrder: any = {};

                    if (creatingNewPurchaseOrder) {
                        let bxContactId: string | undefined | null;
                        if (contactBasedOnBill) {
                            bxContactId = contactBasedOnBill.iD_Contact;
                        } else if (createOrderFromBill.jobItemId && jobItemsMap.has(createOrderFromBill.jobItemId)) {
                            const jobItem = jobItemsMap.get(createOrderFromBill.jobItemId)!;
                            if (jobItem.contactId) {
                                bxContactId = jobItem.contactId;
                            } else if (jobItem.iD_QuoteRequest && jobItem.iD_QuoteRequestItem) {
                                bxContactId = await this.#getContactByQuoteRequest(jobItem.iD_QuoteRequest);
                            } else if (jobItem.iD_EstimateItem) {
                                bxContactId = await this.#getContactByEstimateItem(jobItem.iD_EstimateItem);
                            }
                        }

                        purchaseOrder = {
                            ID_Contact: bxContactId!,
                            Description: createOrderFromBill.description?.substring(0, 100) ?? '',
                            OrderStatus: 'Received',
                            AccSyncData: 'Imported',
                            CreationSource: PurchaseOrderCreationSource.IMPORTEDBILLS,
                        };
                    } else {
                        purchaseOrder = await this.purchaseOrdersService.getPurchaseOrder(purchaseOrderId, job.iD_Job).then((res) => res);
                    }

                    const poInvoice = {
                        ID_Job: createOrderFromBill.jobId,
                        InvoiceDate: createOrderFromBill.date,
                        Reference: createOrderFromBill.reference,
                        InvoiceType: 'Invoice',
                        TaxRates: job?.taxRates,
                        items: [] as PoInvoiceItem[],
                        AccSyncData: 'Imported',
                    };

                    const poItems = [] as PoItem[];

                    createOrderFromBill.items.forEach((i) => {
                        const category = i.categoryId && jobItemsMap.has(i.categoryId) ? jobItemsMap.get(i.categoryId) : null;
                        const jobItem = i.jobItemId && jobItemsMap.has(i.jobItemId) ? jobItemsMap.get(i.jobItemId) : null;
                        const poInvoiceItem = {
                            Quantity: i.quantity,
                            UnitCost: i.unitCost,
                            TaxRates: JSON.stringify([i.taxRates]),
                            CostItemType: jobItem?.costItemType ?? '0',
                        };

                        poInvoice.items.push(poInvoiceItem);

                        poItems.push({
                            ...poInvoiceItem,
                            Description: jobItem?.description ?? i.description?.substring(0, 255) ?? '',
                            ID_CostItem: jobItem?.iD_CostItem,
                            UOM: jobItem?.uOM,
                            ID_EstimateItem: jobItem?.iD_EstimateItem,
                            ID_JobItem: jobItem?.iD_JobItem,
                            Notes: jobItem?.notes,
                            ParentTask: category ? category.description : i.categoryName,
                            ID_ParentTask: category ? category.iD_JobItem : null,
                            ItemCode: jobItem?.itemCode,
                            AccSyncData: 'Imported',
                        });
                    });

                    const breezeJob = await this.#getJob(job.iD_Job);

                    const importSettings = {
                        copyAttachmentsOnCreate: createOrderFromBill.copyAttachmentsOnCreate,
                        invoiceId: createOrderFromBill.accountingId,
                        provider: createOrderFromBill.provider,
                        importingIntoExistingOrder: !creatingNewPurchaseOrder,
                    };

                    // Hack to force loading of existing job item into memory
                    // so Breeze doesn't create a new cost item even though
                    // it's been given the existing job item
                    return this.jobsRepo.GetJobItems(job.iD_Job).then(() => {
                        if (creatingNewPurchaseOrder) {
                            return this.purchaseOrdersService.newOrder(breezeJob, purchaseOrder, poItems, poInvoice, importSettings);
                        }

                        let count = purchaseOrder.PurchaseOrderItems.length;
                        poItems.forEach((item) => {
                            const orderItem = this.jobsRepo.CreatePurchaseOrderItem(purchaseOrder, item);
                            orderItem.Order = count;
                            orderItem.CostItemType = item.CostItemType;
                            orderItem.TaxContent = item.TaxContent;
                            orderItem.TaxRates = item.TaxRates;
                            orderItem.ImportedIntoExistingOrder = true;
                            orderItem.AccSyncData = item.AccSyncData;
                            count = count + 1;
                            // refresh totals
                            orderItem.calculateTotal();

                            if (poInvoice) {
                                const newInvoice = this.jobsRepo.CreatePurchaseOrderInvoice(purchaseOrder, poInvoice);
                                const invoiceItem = {
                                    Quantity: orderItem.Quantity,
                                    UnitCost: orderItem.UnitCost,
                                    TaxRates: orderItem.TaxRates,
                                    TaxContent: orderItem.TaxContent,
                                    CostItemType: orderItem.CostItemType,
                                };

                                const newInvoiceItem = this.jobsRepo.CreatePurchaseOrderInvoiceItem(newInvoice, invoiceItem);
                                newInvoiceItem.ID_PurchaseOrderItem = orderItem.ID_PurchaseOrderItem;
                                newInvoiceItem.TaxRates = orderItem.TaxRates;
                                newInvoiceItem.calculateTotal();
                            }
                        });

                        return this.purchaseOrdersService.viewOrder(purchaseOrder, importSettings);
                    });
                })
            );
    }

    async #getJob(jobId: Uuid) {
        return this.jobsRepo.GetJob(jobId).then((data) => data?.results?.[0] as unknown as Job);
    }

    async #getContactByEstimateItem(estimateItemId: Uuid) {
        const getEstimateItemResult = await firstValueFrom(
            this.graphqlGateway.query({
                query: GetEstimateItem,
                variables: {
                    estimateItemId: estimateItemId,
                },
                fetchPolicy: 'no-cache',
                errorPolicy: 'all',
            })
        );

        const estimateId = getEstimateItemResult?.data?.estimateItemById?.iD_Estimate;

        if (!estimateId) return;

        const getSelectedRecipientResult = await firstValueFrom(
            this.#quoteRequestRecipientApiService.getFirstSelectedRecipientForEstimate(estimateId)
        );
        return getSelectedRecipientResult.data?.quoteRequestRecipientsV2?.items?.[0]?.iD_Contact;
    }

    async #getContactByQuoteRequest(quoteRequestId: string) {
        const getSelectedRecipientResult = await firstValueFrom(
            this.#quoteRequestRecipientApiService.getSelectedRecipientByQuoteRequestId(quoteRequestId as Uuid)
        );
        return getSelectedRecipientResult.data?.quoteRequestRecipientsV2?.items?.[0]?.iD_Contact;
    }
}

export interface CreateOrderFromBill {
    date?: DateTime | null;
    jobId: Uuid;
    jobItemId?: Uuid;
    accountingId: string;
    contactId?: string;
    lineItemId?: string;
    quantity?: number;
    unitCost?: number;
    description?: string | null;
    provider: IntegrationProvider;
    copyAttachmentsOnCreate: boolean;
    importingIntoExistingOrder: boolean;
    reference: string;
    items: CreateOrderItemFromBillItem[];
}

export interface CreateOrderItemFromBillItem {
    lineItemId: string;
    accountingId: string;
    categoryId?: Uuid | null;
    categoryName?: string;
    jobItemId?: Uuid | null;
    description?: string | null;
    quantity: number;
    unitCost: number;
    taxRates?: string | null;
}

type PoInvoiceItem = {
    Quantity: number;
    UnitCost: number;
    TaxRates?: string | null;
    TaxContent?: string | null;
    CostItemType?: string | null;
};

type PoItem = PoInvoiceItem & {
    ID_JobItem?: Uuid;
    Description?: string | null;
    ID_CostItem?: Uuid | null;
    UOM?: string | null;
    ID_EstimateItem?: string | null;
    Notes?: string | null;
    ParentTask?: string | null;
    ID_ParentTask?: string | null;
    ItemCode?: string | null;
    AccSyncData: string | null;
};
